// The session-only top-down REFERENCE PICTURE (Model mode): a transparent,
// view-aligned overlay on a face of the work volume that you position / scale /
// rotate, then carve the block to match. This slice owns the ReferenceImage
// instance, its session state (editing / hidden / opacity), and the
// reference-transform entries that ride the MODEL undo timeline. The actual
// pointer drag of the image lives in desktop-input (it shares the gesture
// pipeline); this slice hands it the instance + a commit hook.

import * as THREE from "three";
import { ReferenceImage } from "../view/reference-image";
import { type ReferenceData } from "../model/saved-model";
import { commitReferenceChange, onReferenceApply } from "../model/history";
import { volumeBounds } from "./work-volume-bounds";

export interface ReferenceDeps {
  gridGroup: THREE.Group; // the overlay frame rides the canvas
  onChange: () => void; // refresh the desktop sidebar (reference buttons/opacity)
  wakeRendering: () => void; // async image decode can finish past the idle window
}

export interface ReferenceApi {
  referenceImage: ReferenceImage; // shared with desktop-input (pointer drag) + AppContext
  restoreReference: (data: ReferenceData | undefined) => void;
  commitReference: () => void;
  clearReferenceImage: () => void;
  editingReference: () => boolean;
  serialize: () => ReferenceData | undefined;
  // Import: caller computes the face normal (it owns the camera + axis table)
  // then hands the decoded image over; this finishes the placement + state.
  importLoaded: (img: HTMLImageElement, src: string, normal: THREE.Vector3) => void;
  toggleReferenceEdit: () => void;
  toggleReferenceHidden: () => void;
  setReferenceOpacity: (v: number) => void;
  referenceOn: () => boolean; // hasImage
  referenceEditingOn: () => boolean;
  referenceHiddenOn: () => boolean;
  referenceOpacityValue: () => number;
  // Per-frame: apply visibility + edit-mode from the current state; returns
  // whether the image is in edit mode (so the caller routes LMB to the image
  // drag instead of the voxel tools).
  updateVisibility: () => boolean;
}

export function installReference(deps: ReferenceDeps): ReferenceApi {
  const referenceImage = new ReferenceImage();
  deps.gridGroup.add(referenceImage.frame);
  let referenceEditing = false; // while true, LMB transforms the image, not voxels
  let referenceHidden = false; // manual hide toggle (independent of mode gating)
  let referenceOpacity = 0.5;

  // Reference-image transform history lives on the MODEL timeline: each
  // move/scale/rotate gesture commits a `reference` entry onto the same undo
  // stack as the voxel edits (model/history.ts), so Ctrl+Z reverses whichever
  // action actually came last. refCurrentJson is the last committed transform —
  // the `before` of the next commit. Loading/importing a picture only resets
  // this baseline; earlier entries stay on the stack and degrade gracefully
  // (undoing past an import re-applies the old transform to the current image).
  let refCurrentJson = "";

  function resetReferenceBaseline(): void {
    refCurrentJson = referenceImage.hasImage() ? referenceImage.transformJSON() : "";
  }
  function commitReference(): void {
    const snap = referenceImage.hasImage() ? referenceImage.transformJSON() : "";
    if (snap === refCurrentJson) return;
    commitReferenceChange(refCurrentJson, snap);
    refCurrentJson = snap;
  }
  onReferenceApply((json) => {
    if (json && referenceImage.hasImage()) referenceImage.applyTransformJSON(json);
    refCurrentJson = json;
  });

  // Load a saved reference picture (or clear if none) when switching models.
  function restoreReference(data: ReferenceData | undefined): void {
    referenceEditing = false;
    referenceHidden = false;
    if (!data) {
      referenceImage.clear();
      resetReferenceBaseline();
      return;
    }
    referenceOpacity = data.opacity;
    const img = new Image();
    img.onload = () => {
      referenceImage.loadSaved(data, img);
      resetReferenceBaseline();
      deps.onChange();
      deps.wakeRendering(); // async image decode can finish past the idle window
    };
    img.src = data.src;
  }

  // Remove the reference image entirely (Backspace while editing it).
  function clearReferenceImage(): void {
    referenceImage.clear();
    resetReferenceBaseline();
    referenceEditing = false;
    deps.onChange();
  }

  function editingReference(): boolean {
    return referenceEditing && referenceImage.hasImage();
  }

  // Finish an import: the freshly decoded image, placed on the chosen face,
  // becomes the new baseline and jumps straight into align mode.
  function importLoaded(img: HTMLImageElement, src: string, normal: THREE.Vector3): void {
    referenceImage.load(img, { ...volumeBounds }, src, normal);
    referenceImage.setOpacity(referenceOpacity);
    referenceHidden = false;
    resetReferenceBaseline(); // the imported placement is the new baseline
    referenceEditing = true; // jump straight into align mode
    deps.onChange();
    deps.wakeRendering(); // file dialog + decode can outlast the idle window
  }

  function updateVisibility(): boolean {
    // The reference overlay + its edit handles appear in both 2D and 3D (it's a
    // view-aligned plane on a face of the volume), unless manually hidden.
    const refVisible = referenceImage.hasImage() && !referenceHidden;
    referenceImage.group.visible = refVisible;
    const refMode = referenceEditing && refVisible;
    referenceImage.setEditMode(refMode);
    return refMode;
  }

  return {
    referenceImage,
    restoreReference,
    commitReference,
    clearReferenceImage,
    editingReference,
    serialize: () => referenceImage.serialize() ?? undefined,
    importLoaded,
    toggleReferenceEdit: () => {
      referenceEditing = !referenceEditing;
      deps.onChange();
    },
    toggleReferenceHidden: () => {
      referenceHidden = !referenceHidden;
      if (referenceHidden) referenceEditing = false; // can't edit a hidden image
      deps.onChange();
    },
    setReferenceOpacity: (v) => {
      referenceOpacity = v;
      referenceImage.setOpacity(v);
    },
    referenceOn: () => referenceImage.hasImage(),
    referenceEditingOn: () => referenceEditing,
    referenceHiddenOn: () => referenceHidden,
    referenceOpacityValue: () => referenceOpacity,
    updateVisibility,
  };
}
